import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ModalBase from '@/components/Modal/ModalBase.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import Field from '@/components/common/Field.jsx';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { etiquetasEmitidas } from '@/services/etiquetas.js';
import { imprimirEtiquetas } from '@/services/printing/labelPrinting.js';
import {
  CargasPage,
  CargasSection,
  CargasStickyActions,
  CargasTableShell,
  CargasViewport,
  cargasButtonClassName,
  cargasInputClassName,
  cargasPrimaryButtonClassName,
} from './components/CargasLayout.jsx';

const INITIAL_FILTERS = {
  search: '',
  desde: '',
  hasta: '',
  remito: '',
  recepcionId: '',
  guiaId: '',
  loteId: '',
};
const LOTE_PAGE_SIZE = 500;

/** Filters come from the query string (links from guías/lotes) or from navigation state. */
function filtersFromLocation(location) {
  const params = new URLSearchParams(location.search || '');
  const fromQuery = Object.fromEntries(
    Object.keys(INITIAL_FILTERS).filter(key => params.get(key)).map(key => [key, params.get(key)]),
  );
  return { ...INITIAL_FILTERS, ...(location.state?.initialFilters || {}), ...fromQuery };
}

function toQueryFilters(filters) {
  return Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== '' && value != null));
}

function downloadZpl(codigo, zpl) {
  if (!zpl) return;
  const blob = new Blob([zpl], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${codigo}.zpl.txt`;
  link.click();
  URL.revokeObjectURL(url);
}

const bultoLabel = row => (row.bultoNro ? `${row.bultoNro}/${row.bultosTotal || row.bultoNro}` : row.totBultos);

export default function EtiquetasListado() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, tenant } = useAuth();
  const [draft, setDraft] = useState(() => filtersFromLocation(location));
  const [applied, setApplied] = useState(() => filtersFromLocation(location));
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ items: [], pageInfo: { total: 0, pages: 1 } });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [printStatus, setPrintStatus] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [activeZpl, setActiveZpl] = useState(null);

  const pageSize = applied.loteId ? LOTE_PAGE_SIZE : 20;

  // Re-read filters only when the user navigates here again (e.g. from a guía); the
  // first render already used them, so it must not trigger a second query.
  const lastLocationKey = useRef(location.key);
  useEffect(() => {
    if (lastLocationKey.current === location.key) return;
    lastLocationKey.current = location.key;
    const next = filtersFromLocation(location);
    setDraft(next);
    setApplied(next);
    setPage(1);
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await etiquetasEmitidas(toQueryFilters(applied), { page, pageSize });
      setResult(data);
      setSelected(new Set());
    } catch (err) {
      setError(err.message || 'No se pudo cargar el listado de etiquetas.');
    } finally {
      setLoading(false);
    }
  }, [applied, page, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totalPages = useMemo(() => result.pageInfo.pages || 1, [result.pageInfo.pages]);
  const selectedRows = useMemo(() => result.items.filter(row => selected.has(row.id)), [result.items, selected]);

  const handleFilterChange = event => {
    const { name, value } = event.target;
    setDraft(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setPage(1);
    setApplied({ ...draft });
  };

  const toggleRow = id => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /** Reprints are allowed but always carry a reason in the label's audit trail. */
  const printRows = async rows => {
    if (!rows.length) return;
    let motivo;
    const reimpresiones = rows.filter(row => Number(row.impresiones) > 0);
    if (reimpresiones.length) {
      const answer = window.prompt(
        `${reimpresiones.length} etiqueta(s) ya fueron impresas. Indicá el motivo de la reimpresión:`,
        'Etiqueta dañada',
      );
      if (answer == null) return;
      motivo = answer.trim() || 'Reimpresión';
    }
    setPrinting(true);
    setPrintStatus(null);
    try {
      const outcome = await imprimirEtiquetas(rows, { motivo, scope: { user, tenant } });
      setPrintStatus(
        outcome.failed?.length
          ? { type: 'warning', message: `Se enviaron ${outcome.count} de ${rows.length}. Fallaron: ${outcome.failed.map(item => `${item.codigoEtiqueta || item.id} (${item.message})`).join(', ')}.` }
          : { type: 'success', message: `${outcome.count} etiqueta(s) enviadas a ${outcome.printerName}.` },
      );
      await loadData();
    } catch (err) {
      setPrintStatus({ type: 'error', message: err.message || 'No se pudieron imprimir las etiquetas.' });
    } finally {
      setPrinting(false);
    }
  };

  const printLote = async loteId => {
    try {
      const lote = await etiquetasEmitidas({ loteId }, { page: 1, pageSize: LOTE_PAGE_SIZE });
      await printRows(lote.items);
    } catch (err) {
      setPrintStatus({ type: 'error', message: err.message || 'No se pudo cargar el lote.' });
    }
  };

  return (
    <CargasPage
      title="Etiquetas emitidas"
      description="Una etiqueta por bulto. Imprimí, reimprimí con motivo o filtrá por lote y guía."
      breadcrumbs={[{ label: 'Cargas', href: '/cargas/dashboard' }, { label: 'Etiquetas' }]}
      hideHeaderOnCompact
      footer={
        <CargasStickyActions
          summary={
            <div className="flex flex-wrap items-center gap-3 text-xs text-[rgba(var(--muted),0.92)]">
              <span>Total: {result.pageInfo.total || 0}</span>
              <span>Pagina {page} de {totalPages}</span>
              {selected.size ? <span>{selected.size} seleccionada(s)</span> : null}
            </div>
          }
          actions={
            <>
              <button
                type="button"
                className={cargasPrimaryButtonClassName}
                onClick={() => printRows(selectedRows)}
                disabled={!selectedRows.length || printing}
              >
                Imprimir seleccionadas
              </button>
              <button type="button" className={cargasButtonClassName} onClick={() => setPage(prev => Math.max(1, prev - 1))} disabled={page <= 1}>
                Anterior
              </button>
              <button
                type="button"
                className={cargasButtonClassName}
                onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages}
              >
                Siguiente
              </button>
              <button type="button" className={cargasButtonClassName} onClick={loadData}>
                Actualizar
              </button>
            </>
          }
        />
      }
    >
      <CargasViewport className="grid grid-cols-12 gap-3">
        <CargasSection title="Filtros" className="col-span-12 lg:col-span-3" bodyClassName="p-4">
          <form
            className="grid grid-cols-12 gap-2"
            onSubmit={event => {
              event.preventDefault();
              applyFilters();
            }}
          >
            <div className="col-span-12">
              <Field label="Buscar (cliente, etiqueta o guía)" htmlFor="search">
                <input id="search" name="search" value={draft.search} onChange={handleFilterChange} className={cargasInputClassName} />
              </Field>
            </div>
            <div className="col-span-12 md:col-span-6 lg:col-span-12">
              <Field label="Remito" htmlFor="remito">
                <input id="remito" name="remito" value={draft.remito} onChange={handleFilterChange} className={cargasInputClassName} />
              </Field>
            </div>
            <div className="col-span-6 lg:col-span-6">
              <Field label="Lote" htmlFor="loteId">
                <input id="loteId" name="loteId" value={draft.loteId} onChange={handleFilterChange} className={cargasInputClassName} />
              </Field>
            </div>
            <div className="col-span-6 lg:col-span-6">
              <Field label="Guía (id)" htmlFor="guiaId">
                <input id="guiaId" name="guiaId" value={draft.guiaId} onChange={handleFilterChange} className={cargasInputClassName} />
              </Field>
            </div>
            <div className="col-span-6">
              <Field label="Desde" htmlFor="desde">
                <input id="desde" name="desde" type="date" value={draft.desde} onChange={handleFilterChange} className={cargasInputClassName} />
              </Field>
            </div>
            <div className="col-span-6">
              <Field label="Hasta" htmlFor="hasta">
                <input id="hasta" name="hasta" type="date" value={draft.hasta} onChange={handleFilterChange} className={cargasInputClassName} />
              </Field>
            </div>
            <div className="col-span-12 flex flex-wrap gap-2">
              <button type="submit" className={cargasPrimaryButtonClassName}>
                Buscar
              </button>
              <button
                type="button"
                className={cargasButtonClassName}
                onClick={() => {
                  setDraft(INITIAL_FILTERS);
                  setApplied(INITIAL_FILTERS);
                  setPage(1);
                  if (location.search) navigate('/cargas/etiquetas', { replace: true });
                }}
              >
                Limpiar
              </button>
            </div>
          </form>
        </CargasSection>

        <CargasSection
          title="Resultados"
          description={loading ? 'Buscando etiquetas...' : `${result.pageInfo.total || 0} etiqueta(s) encontradas`}
          className="col-span-12 lg:col-span-9"
          bodyClassName="min-h-0 p-4"
        >
          {printStatus ? (
            <div
              role={printStatus.type === 'error' ? 'alert' : 'status'}
              className="mb-2 rounded-lg border border-[rgba(var(--border),0.45)] px-3 py-2 text-sm text-[rgb(var(--fg))]"
            >
              {printStatus.message}
            </div>
          ) : null}
          {error ? <div role="alert" className="text-sm text-[rgb(var(--brand))]">{error}</div> : null}
          {!error && result.items.length === 0 && !loading ? (
            <EmptyState title="Sin etiquetas" description="Proba ajustando los filtros o emiti nuevas etiquetas." />
          ) : null}
          {result.items.length > 0 ? (
            <CargasTableShell className="max-h-[60vh]">
              <table className="min-w-full text-left text-sm">
                <thead className="sticky top-0 bg-[rgb(var(--panel))] text-[rgba(var(--muted),0.9)]">
                  <tr>
                    <th className="px-2 py-2">
                      <input
                        type="checkbox"
                        aria-label="Seleccionar todas"
                        checked={selected.size > 0 && selected.size === result.items.length}
                        onChange={event => setSelected(event.target.checked ? new Set(result.items.map(row => row.id)) : new Set())}
                      />
                    </th>
                    <th className="px-3 py-2">Etiqueta</th>
                    <th className="px-3 py-2">Bulto</th>
                    <th className="px-3 py-2">Guía</th>
                    <th className="px-3 py-2">Remitente - Destinatario</th>
                    <th className="px-3 py-2">Fecha</th>
                    <th className="px-3 py-2">Impresiones</th>
                    <th className="px-3 py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {result.items.map(row => (
                    <tr key={row.id} className="border-t border-[rgba(var(--border),0.45)]">
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          aria-label={`Seleccionar ${row.codigoEtiqueta}`}
                          checked={selected.has(row.id)}
                          onChange={() => toggleRow(row.id)}
                        />
                      </td>
                      <td className="px-3 py-2 font-semibold">{row.codigoEtiqueta}</td>
                      <td className="px-3 py-2">{bultoLabel(row)}</td>
                      <td className="px-3 py-2">{row.guiaNumero || <span className="text-[rgba(var(--muted),0.92)]">Pendiente</span>}</td>
                      <td className="px-3 py-2">
                        {row.remitenteNombre} - {row.destinatarioNombre}
                      </td>
                      <td className="px-3 py-2">
                        {row.fechaHoraEmision ? new Date(row.fechaHoraEmision).toLocaleString('es-AR') : '-'}
                      </td>
                      <td className="px-3 py-2">{row.impresiones ?? 0}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <button type="button" className={cargasButtonClassName} disabled={printing} onClick={() => printRows([row])}>
                            {Number(row.impresiones) > 0 ? 'Reimprimir' : 'Imprimir'}
                          </button>
                          {row.loteId ? (
                            <button type="button" className={cargasButtonClassName} disabled={printing} onClick={() => printLote(row.loteId)}>
                              Imprimir lote
                            </button>
                          ) : null}
                          <button type="button" className={cargasButtonClassName} onClick={() => setActiveZpl(row)}>
                            Ver ZPL
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CargasTableShell>
          ) : null}
        </CargasSection>
      </CargasViewport>

      <ModalBase isOpen={Boolean(activeZpl)} onClose={() => setActiveZpl(null)} labelledBy="zpl-modal">
        <div className="space-y-4">
          <h2 id="zpl-modal" className="text-lg font-semibold text-[rgb(var(--fg))]">
            ZPL - {activeZpl?.codigoEtiqueta}
          </h2>
          <textarea
            readOnly
            value={activeZpl?.zpl || ''}
            className="h-64 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--bg),0.6)] p-3 text-xs text-[rgb(var(--fg))]"
          />
          <div className="flex justify-end gap-2">
            <button className={cargasButtonClassName} onClick={() => setActiveZpl(null)}>
              Cerrar
            </button>
            <button
              className={cargasPrimaryButtonClassName}
              onClick={() => downloadZpl(activeZpl?.codigoEtiqueta, activeZpl?.zpl)}
            >
              Descargar
            </button>
          </div>
        </div>
      </ModalBase>
    </CargasPage>
  );
}
